import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import {ProgressInfinite} from '@brandwatch/axiom-components';
import {getPrivateLink} from '../lib/tycho';
import history from '../history.js';

import * as storage from '../lib/storage';

import PrivateLinkRequestInvite from './PrivateLinkRequestInvite.jsx';

/**
 * If the user has viewed this private link before then they will have already
 * gone through the email auth workflow (whereby they enter their email address
 * and receive an invite link via email).
 *
 * When an invite link is accessed, a copy of the invite object is placed in
 * local storage. Therefore, the invite can be looked up and, if found, used
 * to redirect the user to the invite link they need to use to see the deck.
 *
 * This function finds the invite, if it exists.
 */
function getExistingInvite(deckId) {
  const invitesById = storage.get('invites');
  const usableInviteId = Object.keys(invitesById).find(inviteId => {
    const invite = invitesById[inviteId];

    const expiryDate = !!invite.expiry && new Date(invite.expiry).getTime();
    const nowDate = Date.now();
    const hasExpired = Number.isFinite(expiryDate) && (nowDate > expiryDate);
    const isPrivateLinkInvite = invite.deck_share_type === 'private_link';
    const isForDeckBeingShared = invite.deck_id === deckId;

    return isPrivateLinkInvite && isForDeckBeingShared && !hasExpired;
  });

  return usableInviteId ? invitesById[usableInviteId] : null;
}

const PrivateLinkEntryPoint = ({
  match: {
    params: {
      privateLinkIdHash
    }
  }
}) => {
  const [privateLink, setPrivateLink] = useState();
  const [askForEmailAddress, setAskForEmailAddress] = useState(false);

  const dispatch = useDispatch();

  // Don't show an error bar at the top of the screen when something goes wrong;
  // in those instances we just want to direct the user through the private link
  // auth flow.
  useEffect(() => {
    dispatch({
      type: 'ALERT_BAR_DISABLE'
    });
  }, [dispatch]);

  // Request basic information about the private link.
  // This is needed to know whether the private link exists / is enabled,
  // and so that the deck's name can be displayed.
  useEffect(() => {
    getPrivateLink(privateLinkIdHash)
      .then(privateLink => {
        setPrivateLink(privateLink);

        dispatch({
          type: 'DECK_ORDER',
          payload: [privateLink.deck_id],
        });
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch private link', err);
        setPrivateLink({enabled: false});
      });
  }, [
    dispatch,
    privateLinkIdHash,
  ]);

  useEffect(function processPrivateLink() {
    if (!privateLink) {
      return;
    }

    const invite = getExistingInvite(privateLink.deck_id);

    // When the private link has been accessed before the user can just be
    // redirected to the appropriate invite URL. Otherwise, they have to
    // go through the email auth workflow.
    if (invite) {
      history.replace(`/i/${invite.invite_id_hash}?otp=${invite.otp}`);
    } else {
      setAskForEmailAddress(true);
    }
  }, [
    privateLink,
  ]);

  // Show spinner whilst loading.
  if (!privateLink || !askForEmailAddress) {
    return (
      <div className="Invite">
        <ProgressInfinite color="white"/>
      </div>
    );
  }

  // The user doesn't have an existing invite, so ask them for their email
  // address and then send them an email invite.
  return (
    <PrivateLinkRequestInvite
      privateLinkIdHash={privateLinkIdHash}
    />
  );
};

PrivateLinkEntryPoint.propTypes = {
  match: PropTypes.object,
};

export default PrivateLinkEntryPoint;
